import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Download, AlertCircle, CheckCircle, Calendar, DollarSign, FileText, TrendingUp, Shield, Users, Scale, Bell, Info, AlertTriangle, BookOpen, Lock } from 'lucide-react';
import api from '../utils/api';
import { toast } from 'sonner';

// Helper to render any value safely - always returns a string or JSX-safe value
const renderValue = (value) => {
  if (value === null || value === undefined || value === '') return 'No especificado';
  if (typeof value === 'boolean') return value ? 'Si' : 'No';
  if (typeof value === 'string') return value;
  if (typeof value === 'number') return value.toString();
  if (Array.isArray(value)) {
    if (value.length === 0) return 'No especificado';
    // Join strings, stringify objects
    return value.map(v => typeof v === 'string' ? v : (typeof v === 'object' && v !== null ? JSON.stringify(v) : String(v))).join(', ');
  }
  if (typeof value === 'object') {
    // Convert object to readable string
    return Object.entries(value)
      .filter(([, v]) => v !== null && v !== undefined && v !== '')
      .map(([k, v]) => `${k.replace(/_/g, ' ')}: ${typeof v === 'object' ? JSON.stringify(v) : v}`)
      .join(' | ') || 'No especificado';
  }
  return String(value);
};

// Section component
const Section = ({ title, icon: Icon, children, iconColor = '#1A4D2E', className = '' }) => (
  <div className={`bg-white border border-border shadow-sm rounded-lg p-6 mb-6 ${className}`}>
    <div className="flex items-center gap-3 mb-4">
      <div className="p-2 rounded-lg" style={{ backgroundColor: `${iconColor}15` }}>
        <Icon className="w-5 h-5" style={{ color: iconColor }} />
      </div>
      <h2 className="text-lg font-semibold" style={{ fontFamily: 'Merriweather, serif', color: '#1A4D2E' }}>{title}</h2>
    </div>
    {children}
  </div>
);

// Info card for metrics
const MetricCard = ({ label, value, color = '#1A4D2E' }) => {
  const displayValue = renderValue(value);
  if (!displayValue || displayValue === 'No especificado' || displayValue === 'No especificado en el documento') return null;
  
  return (
    <div className="p-4 rounded-lg border" style={{ borderColor: `${color}30`, backgroundColor: `${color}08` }}>
      <p className="text-xs font-medium text-gray-500 mb-1">{label}</p>
      <p className="text-sm font-semibold" style={{ color }}>{String(displayValue)}</p>
    </div>
  );
};

// Risk badge
const RiskBadge = ({ level }) => {
  const colors = {
    'alto': { bg: '#FEE2E2', text: '#991B1B', border: '#F87171' },
    'medio': { bg: '#FEF3C7', text: '#92400E', border: '#FBBF24' },
    'bajo': { bg: '#D1FAE5', text: '#065F46', border: '#34D399' }
  };
  const l = (level || 'medio').toLowerCase();
  const style = colors[l] || colors['medio'];
  
  return (
    <span 
      className="text-xs font-medium px-2 py-1 rounded-full border"
      style={{ backgroundColor: style.bg, color: style.text, borderColor: style.border }}
    >
      Riesgo {level}
    </span>
  );
};

// List item component
const ListItem = ({ item, icon: Icon = CheckCircle, iconColor = '#1A4D2E' }) => {
  if (!item) return null;
  if (typeof item === 'string') {
    return (
      <li className="flex items-start gap-3 py-2">
        <Icon className="w-4 h-4 flex-shrink-0 mt-1" style={{ color: iconColor }} />
        <span className="text-sm text-gray-700">{item}</span>
      </li>
    );
  }
  if (typeof item === 'object' && item !== null) {
    return (
      <li className="py-3 px-4 bg-gray-50 rounded-lg mb-2">
        {Object.entries(item).map(([key, value]) => {
          const displayValue = renderValue(value);
          if (displayValue === 'No especificado') return null;
          return (
            <div key={key} className="mb-1 last:mb-0">
              <span className="text-xs font-medium text-gray-500">{key.replace(/_/g, ' ')}: </span>
              <span className="text-sm text-gray-800">{displayValue}</span>
            </div>
          );
        })}
      </li>
    );
  }
  return (
    <li className="flex items-start gap-3 py-2">
      <Icon className="w-4 h-4 flex-shrink-0 mt-1" style={{ color: iconColor }} />
      <span className="text-sm text-gray-700">{String(item)}</span>
    </li>
  );
};

const AnalysisResults = () => {
  const { id } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [analysis, setAnalysis] = useState(null);
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState(null);
  const [activeTab, setActiveTab] = useState('resumen');

  useEffect(() => {
    if (!user) {
      navigate('/login');
      return;
    }
    fetchAnalysis();
  }, [id, user]);

  const fetchAnalysis = async () => {
    try {
      const data = await api.analyzeContract(id);
      setAnalysis(data);
    } catch (error) {
      if (error.response?.status === 402) {
        toast.error('Debes pagar para ver el análisis');
        navigate(`/payment?contract_id=${id}&type=analysis`);
      } else {
        toast.error('Error al cargar el análisis');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleDownload = async (format) => {
    setDownloading(format);
    try {
      const blobData = format === 'pdf' ? await api.downloadPDF(id) : await api.downloadExcel(id);
      const mimeType = format === 'pdf' ? 'application/pdf' : 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
      const blob = new Blob([blobData], { type: mimeType });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `analisis_contrato.${format === 'pdf' ? 'pdf' : 'xlsx'}`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      toast.success(`Reporte ${format.toUpperCase()} descargado`);
    } catch (error) {
      let errorMsg = 'Error al descargar';
      if (error.response?.data instanceof Blob) {
        try {
          const text = await error.response.data.text();
          const parsed = JSON.parse(text);
          errorMsg = parsed.detail || errorMsg;
        } catch { /* ignore */ }
      }
      toast.error(errorMsg);
    } finally {
      setDownloading(null);
    }
  };

  if (!user) return null;

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: '#F5F5F0' }}>
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 mx-auto mb-4" style={{ borderColor: '#1A4D2E' }}></div>
          <p className="text-gray-600">Analizando contrato con IA...</p>
        </div>
      </div>
    );
  }

  if (!analysis) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: '#F5F5F0' }}>
        <div className="text-center">
          <FileText className="w-16 h-16 mx-auto mb-4 text-gray-400" />
          <p className="text-lg mb-4">Análisis no encontrado</p>
          <Link to="/dashboard"><Button>Volver al Dashboard</Button></Link>
        </div>
      </div>
    );
  }

  // Extract data with fallbacks
  const identificacion = analysis.identificacion_contrato || {};
  const resumen = analysis.resumen_ejecutivo || {};
  const financiero = analysis.analisis_financiero || {};
  const clausulasRiesgo = Array.isArray(analysis.clausulas_riesgosas) ? analysis.clausulas_riesgosas : [];
  const clausulasAbusivas = Array.isArray(analysis.clausulas_abusivas) ? analysis.clausulas_abusivas : [];
  const derechosObligaciones = analysis.derechos_y_obligaciones || {};
  const privacidad = analysis.privacidad_datos || {};
  const conclusion = analysis.conclusion_recomendacion || {};
  const alertas = Array.isArray(analysis.alertas_criticas) ? analysis.alertas_criticas : [];

  // Get score safely
  const score = typeof conclusion.calificacion === 'number' ? conclusion.calificacion : (parseInt(conclusion.calificacion) || null);
  const scoreColor = score !== null ? (score >= 7 ? '#DC2626' : score >= 4 ? '#D97706' : '#059669') : '#6B7280';

  // Risk score from backend
  const riskScore = analysis.risk_score;

  let riskLevel = "BAJO";

  if (riskScore >= 7) {
  riskLevel = "ALTO";
  } else if (riskScore >= 4) {
  riskLevel = "MEDIO";
  }

  const tabs = [
    { id: 'resumen', label: 'Resumen', icon: FileText },
    { id: 'financiero', label: 'Financiero', icon: DollarSign },
    { id: 'riesgos', label: 'Riesgos', icon: AlertTriangle },
    { id: 'derechos', label: 'Derechos', icon: Scale },
    { id: 'privacidad', label: 'Privacidad', icon: Lock },
  ];

  return (
    <div className="min-h-screen" style={{ backgroundColor: '#F5F5F0' }}>
      {/* Navigation */}
      <nav className="bg-white border-b shadow-sm sticky top-0 z-20">
        <div className="container mx-auto px-4 md:px-8 max-w-7xl">
          <div className="flex items-center justify-between h-14">
            <Link to="/dashboard" className="flex items-center gap-2">
              <img src="/assets/logo.png" alt="logo" className="h-8 w-auto" />
              <span className="text-lg font-semibold" style={{ color: '#1A4D2E' }}>Contractcheck360</span>
            </Link>
            <div className="flex gap-2">
              <Button onClick={() => handleDownload('pdf')} disabled={downloading === 'pdf'} size="sm" style={{ backgroundColor: '#1A4D2E' }}>
                <Download className="w-4 h-4 mr-1" /> PDF
              </Button>
            </div>
          </div>
        </div>
      </nav>

      <div className="container mx-auto px-4 md:px-8 max-w-6xl py-6">
        {/* Header Card */}
        <div className="bg-white border shadow-sm rounded-lg p-6 mb-6">
          <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4">
            <div className="flex-1">
              <div className="flex flex-wrap gap-2 mb-2">
                <span className="text-xs font-medium px-3 py-1 rounded-full bg-gray-100 text-gray-600">
                  {identificacion.tipo_contrato || 'Contrato'}
                </span>
                {identificacion.tipo_adhesion && (
                  <span className="text-xs font-medium px-3 py-1 rounded-full bg-blue-100 text-blue-700">
                    {identificacion.tipo_adhesion}
                  </span>
                )}
              </div>
              <h1 className="text-xl md:text-2xl font-bold mb-1" style={{ color: '#1A4D2E' }}>
                {identificacion.institucion_emisora || 'Análisis de Contrato'}
              </h1>
              {identificacion.explicacion_simple && (
                <p className="text-sm text-gray-600">{identificacion.explicacion_simple}</p>
              )}
              {identificacion.marco_regulatorio && Array.isArray(identificacion.marco_regulatorio) && (
                <div className="flex flex-wrap gap-1 mt-2">
                  {identificacion.marco_regulatorio.map((reg, i) => (
                    <span key={i} className="text-xs px-2 py-0.5 rounded bg-green-50 text-green-700">{reg}</span>
                  ))}
                </div>
              )}
            </div>
            
            {score !== null && (
              <div className="text-center p-4 rounded-xl min-w-[120px]" style={{ backgroundColor: `${scoreColor}10` }}>
                <div className="text-3xl font-bold" style={{ color: scoreColor }}>{score}/10</div>
                <div className="text-xs font-medium mt-1 px-3 py-1 rounded-full inline-block" style={{ backgroundColor: scoreColor, color: 'white' }}>
                  {score >= 7 ? 'Riesgo Alto' : score >= 4 ? 'Riesgo Medio' : 'Riesgo Bajo'}
                </div>
              </div>
            )}
            {riskScore !== null && riskScore !== undefined && (
              <div className="text-center p-4 rounded-xl min-w-[120px]" style={{ backgroundColor: '#F3F4F610' }}>
                <div className="text-3xl font-bold" style={{ color: "#4B5563" }}>{alertas?.length || 0}</div>
                <div className="text-xs font-medium mt-1" data-testid="risks-detected-label">Alertas críticas</div>
              </div>
            )}
          </div>
        </div>

        {/* Critical Alerts */}
        {alertas.length > 0 && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
            <div className="flex items-center gap-2 mb-3">
              <Bell className="w-5 h-5 text-red-600" />
              <h3 className="font-semibold text-red-800">Alertas Críticas</h3>
            </div>
            <ul className="space-y-2">
              {alertas.map((alerta, i) => (
                <li key={i} className="flex items-start gap-2 text-sm text-red-700">
                  <AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5" />
                  <span>{alerta}</span>
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* Tabs */}
        <div className="flex gap-1 mb-6 overflow-x-auto pb-2 bg-white rounded-lg p-1 border">
          {tabs.map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium transition-colors whitespace-nowrap ${
                activeTab === tab.id ? 'text-white' : 'text-gray-600 hover:bg-gray-100'
              }`}
              style={activeTab === tab.id ? { backgroundColor: '#1A4D2E' } : {}}
            >
              <tab.icon className="w-4 h-4" />
              {tab.label}
            </button>
          ))}
        </div>

        {/* Tab Content */}
        {activeTab === 'resumen' && (
          <>
            <Section title="Resumen Ejecutivo" icon={FileText}>
              {resumen.resumen_para_cualquier_persona && (
                <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-4">
                  <p className="text-sm text-green-800 leading-relaxed">{resumen.resumen_para_cualquier_persona}</p>
                </div>
              )}
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                <MetricCard label="¿Qué estás contratando?" value={resumen.que_estas_contratando} />
                <MetricCard label="¿Cuánto vas a pagar?" value={resumen.cuanto_vas_a_pagar} color="#D97706" />
                <MetricCard label="¿Por cuánto tiempo?" value={resumen.por_cuanto_tiempo} />
                <MetricCard label="Obligaciones principales" value={resumen.obligaciones_principales} />
              </div>

              {Array.isArray(resumen.principales_riesgos) && resumen.principales_riesgos.length > 0 && (
                <div className="mt-4">
                  <h4 className="text-sm font-medium text-gray-700 mb-2">Principales Riesgos:</h4>
                  <ul className="space-y-1">
                    {resumen.principales_riesgos.map((riesgo, i) => (
                      <li key={i} className="flex items-start gap-2 text-sm text-red-700">
                        <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" />
                        <span>{riesgo}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </Section>

            {conclusion.justificacion && (
              <Section title="Conclusión y Recomendación" icon={Scale}>
                <p className="text-sm text-gray-700 leading-relaxed mb-4">{conclusion.justificacion}</p>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {conclusion.para_quien_es_adecuado && (
                    <div className="p-3 bg-green-50 rounded-lg">
                      <p className="text-xs font-medium text-green-700 mb-1">Adecuado para:</p>
                      <p className="text-sm text-green-800">{conclusion.para_quien_es_adecuado}</p>
                    </div>
                  )}
                  {conclusion.para_quien_no_es_adecuado && (
                    <div className="p-3 bg-red-50 rounded-lg">
                      <p className="text-xs font-medium text-red-700 mb-1">No recomendado para:</p>
                      <p className="text-sm text-red-800">{conclusion.para_quien_no_es_adecuado}</p>
                    </div>
                  )}
                </div>

                {conclusion.recomendaciones_antes_firmar && Array.isArray(conclusion.recomendaciones_antes_firmar) && (
                  <div className="mt-4">
                    <h4 className="text-sm font-medium text-gray-700 mb-2">Antes de firmar:</h4>
                    <ul className="space-y-1">
                      {conclusion.recomendaciones_antes_firmar.map((rec, i) => (
                        <li key={i} className="flex items-start gap-2 text-sm text-gray-600">
                          <CheckCircle className="w-4 h-4 flex-shrink-0 mt-0.5 text-green-600" />
                          <span>{rec}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </Section>
            )}
          </>
        )}

        {activeTab === 'financiero' && (
          <>
            {/* Interest Rates */}
            {financiero.tasa_interes && typeof financiero.tasa_interes === 'object' && (
              <Section title="Tasas de Interés" icon={TrendingUp}>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
                  <MetricCard label="Ordinaria Anual" value={financiero.tasa_interes.ordinaria_anual} />
                  <MetricCard label="Ordinaria Mensual" value={financiero.tasa_interes.ordinaria_mensual} />
                  <MetricCard label="Moratoria Anual" value={financiero.tasa_interes.moratoria_anual} color="#DC2626" />
                  <MetricCard label="Moratoria Mensual" value={financiero.tasa_interes.moratoria_mensual} color="#DC2626" />
                </div>
                {financiero.tasa_interes.explicacion && (
                  <p className="text-sm text-gray-600 bg-gray-50 p-3 rounded-lg">{financiero.tasa_interes.explicacion}</p>
                )}
              </Section>
            )}

            {/* CAT */}
            {financiero.cat && typeof financiero.cat === 'object' && (
              <Section title="CAT (Costo Anual Total)" icon={DollarSign} iconColor="#D97706">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-4">
                  <MetricCard label="CAT sin IVA" value={financiero.cat.porcentaje} color="#D97706" />
                  <MetricCard label="CAT con IVA" value={financiero.cat.porcentaje_con_iva} color="#D97706" />
                </div>
                {financiero.cat.impacto_real && (
                  <p className="text-sm text-amber-700 bg-amber-50 p-3 rounded-lg">{financiero.cat.impacto_real}</p>
                )}
              </Section>
            )}

            {/* Payment Structure */}
            {financiero.desglose_pagos && typeof financiero.desglose_pagos === 'object' && (
              <Section title="Estructura de Pagos" icon={Calendar}>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mb-4">
                  <MetricCard label="Número de pagos" value={financiero.desglose_pagos.numero_pagos} />
                  <MetricCard label="Monto cada pago" value={financiero.desglose_pagos.monto_cada_pago} />
                  <MetricCard label="Frecuencia" value={financiero.desglose_pagos.frecuencia} />
                  <MetricCard label="Primer pago" value={financiero.desglose_pagos.primer_pago} />
                  <MetricCard label="Último pago" value={financiero.desglose_pagos.ultimo_pago} />
                  <MetricCard label="Monto total" value={financiero.monto_total_a_pagar} color="#1A4D2E" />
                </div>
                {financiero.ejemplo_practico && (
                  <div className="bg-blue-50 border border-blue-200 p-3 rounded-lg">
                    <p className="text-xs font-medium text-blue-700 mb-1">Ejemplo práctico:</p>
                    <p className="text-sm text-blue-800">{financiero.ejemplo_practico}</p>
                  </div>
                )}
              </Section>
            )}

            {/* Commissions */}
            {financiero.comisiones && Array.isArray(financiero.comisiones) && financiero.comisiones.length > 0 && (
              <Section title="Comisiones" icon={DollarSign} iconColor="#D97706">
                <ul className="space-y-2">
                  {financiero.comisiones.map((com, i) => (
                    <ListItem key={i} item={com} icon={DollarSign} iconColor="#D97706" />
                  ))}
                </ul>
              </Section>
            )}

            {/* Penalties */}
            {financiero.penalizaciones && Array.isArray(financiero.penalizaciones) && financiero.penalizaciones.length > 0 && (
              <Section title="Penalizaciones" icon={AlertCircle} iconColor="#DC2626">
                <ul className="space-y-2">
                  {financiero.penalizaciones.map((pen, i) => (
                    <ListItem key={i} item={pen} icon={AlertCircle} iconColor="#DC2626" />
                  ))}
                </ul>
              </Section>
            )}
          </>
        )}

        {activeTab === 'riesgos' && (
          <>
            {/* Risky Clauses */}
            {clausulasRiesgo.length > 0 && (
              <Section title="Cláusulas de Riesgo" icon={AlertTriangle} iconColor="#D97706">
                <div className="space-y-4">
                  {clausulasRiesgo.map((clausula, i) => (
                    <div key={i} className="border rounded-lg p-4 bg-gray-50">
                      <div className="flex items-start justify-between gap-2 mb-2">
                        <h4 className="text-sm font-semibold text-gray-800">
                          {clausula.numero_clausula || `Cláusula ${i + 1}`}
                        </h4>
                        <RiskBadge level={clausula.nivel_riesgo} />
                      </div>
                      {clausula.cita_textual && (
                        <blockquote className="text-xs text-gray-500 italic border-l-2 border-gray-300 pl-3 mb-2">
                          "{clausula.cita_textual}"
                        </blockquote>
                      )}
                      {clausula.que_significa && (
                        <p className="text-sm text-gray-700 mb-1"><strong>Significa:</strong> {clausula.que_significa}</p>
                      )}
                      {clausula.por_que_es_riesgosa && (
                        <p className="text-sm text-red-700"><strong>Riesgo:</strong> {clausula.por_que_es_riesgosa}</p>
                      )}
                    </div>
                  ))}
                </div>
              </Section>
            )}

            {/* Abusive Clauses */}
            {clausulasAbusivas.length > 0 && (
              <Section title="Cláusulas Potencialmente Abusivas" icon={AlertCircle} iconColor="#DC2626">
                <div className="bg-red-50 border border-red-200 rounded-lg p-3 mb-4">
                  <p className="text-xs text-red-700">Estas cláusulas podrían violar la legislación mexicana de protección al consumidor.</p>
                </div>
                <ul className="space-y-2">
                  {clausulasAbusivas.map((ab, i) => (
                    <ListItem key={i} item={ab} icon={AlertCircle} iconColor="#DC2626" />
                  ))}
                </ul>
              </Section>
            )}

            {/* Negotiation points */}
            {conclusion.puntos_negociar && Array.isArray(conclusion.puntos_negociar) && conclusion.puntos_negociar.length > 0 && (
              <Section title="Puntos a Negociar" icon={Info} iconColor="#059669">
                <ul className="space-y-1">
                  {conclusion.puntos_negociar.map((punto, i) => (
                    <li key={i} className="flex items-start gap-2 text-sm text-gray-700">
                      <CheckCircle className="w-4 h-4 flex-shrink-0 mt-0.5 text-green-600" />
                      <span>{punto}</span>
                    </li>
                  ))}
                </ul>
              </Section>
            )}
          </>
        )}

        {activeTab === 'derechos' && (
          <>
            {/* User Obligations */}
            {derechosObligaciones.obligaciones_usuario && Array.isArray(derechosObligaciones.obligaciones_usuario) && (
              <Section title="Tus Obligaciones" icon={BookOpen}>
                <ul className="space-y-1">
                  {derechosObligaciones.obligaciones_usuario.map((ob, i) => (
                    <li key={i} className="flex items-start gap-2 text-sm text-gray-700">
                      <AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5 text-amber-600" />
                      <span>{ob}</span>
                    </li>
                  ))}
                </ul>
              </Section>
            )}

            {/* Institution Obligations */}
            {derechosObligaciones.obligaciones_institucion && Array.isArray(derechosObligaciones.obligaciones_institucion) && (
              <Section title="Obligaciones de la Institución" icon={Users}>
                <ul className="space-y-1">
                  {derechosObligaciones.obligaciones_institucion.map((ob, i) => (
                    <li key={i} className="flex items-start gap-2 text-sm text-gray-700">
                      <CheckCircle className="w-4 h-4 flex-shrink-0 mt-0.5 text-green-600" />
                      <span>{ob}</span>
                    </li>
                  ))}
                </ul>
              </Section>
            )}

            {/* Consumer Rights */}
            {derechosObligaciones.derechos_consumidor && Array.isArray(derechosObligaciones.derechos_consumidor) && (
              <Section title="Tus Derechos como Consumidor" icon={Shield} iconColor="#059669">
                <ul className="space-y-1">
                  {derechosObligaciones.derechos_consumidor.map((der, i) => (
                    <li key={i} className="flex items-start gap-2 text-sm text-green-700">
                      <CheckCircle className="w-4 h-4 flex-shrink-0 mt-0.5" />
                      <span>{der}</span>
                    </li>
                  ))}
                </ul>
              </Section>
            )}

            {/* Cancellation */}
            {derechosObligaciones.condiciones_cancelacion && typeof derechosObligaciones.condiciones_cancelacion === 'object' && (
              <Section title="Condiciones de Cancelacion" icon={Info}>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                  <MetricCard label="Como cancelar" value={derechosObligaciones.condiciones_cancelacion.como_cancelar} />
                  <MetricCard label="Costos" value={derechosObligaciones.condiciones_cancelacion.costos_cancelacion} color="#D97706" />
                  <MetricCard label="Plazo requerido" value={derechosObligaciones.condiciones_cancelacion.plazo_cancelacion} />
                </div>
              </Section>
            )}

            {/* Consequences */}
            {derechosObligaciones.consecuencias_incumplimiento && Array.isArray(derechosObligaciones.consecuencias_incumplimiento) && (
              <Section title="Consecuencias del Incumplimiento" icon={AlertTriangle} iconColor="#DC2626">
                <ul className="space-y-1">
                  {derechosObligaciones.consecuencias_incumplimiento.map((con, i) => (
                    <li key={i} className="flex items-start gap-2 text-sm text-red-700">
                      <AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5" />
                      <span>{con}</span>
                    </li>
                  ))}
                </ul>
              </Section>
            )}
          </>
        )}

        {activeTab === 'privacidad' && (
          <>
            <Section title="Privacidad y Uso de Datos" icon={Lock}>
              <div className="space-y-4">
                {privacidad.datos_recopilados && Array.isArray(privacidad.datos_recopilados) && (
                  <div>
                    <h4 className="text-sm font-medium text-gray-700 mb-2">Datos que recopilan:</h4>
                    <div className="flex flex-wrap gap-2">
                      {privacidad.datos_recopilados.map((dato, i) => (
                        <span key={i} className="text-xs px-2 py-1 rounded-full bg-gray-100 text-gray-700">{dato}</span>
                      ))}
                    </div>
                  </div>
                )}

                {privacidad.uso_datos && Array.isArray(privacidad.uso_datos) && (
                  <div>
                    <h4 className="text-sm font-medium text-gray-700 mb-2">Para qué usan tu información:</h4>
                    <ul className="space-y-1">
                      {privacidad.uso_datos.map((uso, i) => (
                        <li key={i} className="text-sm text-gray-600">• {uso}</li>
                      ))}
                    </ul>
                  </div>
                )}

                {privacidad.terceros && Array.isArray(privacidad.terceros) && privacidad.terceros.length > 0 && (
                  <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
                    <h4 className="text-sm font-medium text-amber-800 mb-2">Comparten con terceros:</h4>
                    <ul className="space-y-1">
                      {privacidad.terceros.map((ter, i) => (
                        <li key={i} className="text-sm text-amber-700">• {ter}</li>
                      ))}
                    </ul>
                  </div>
                )}

                {privacidad.riesgos_privacidad && Array.isArray(privacidad.riesgos_privacidad) && privacidad.riesgos_privacidad.length > 0 && (
                  <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                    <h4 className="text-sm font-medium text-red-800 mb-2">Riesgos de privacidad:</h4>
                    <ul className="space-y-1">
                      {privacidad.riesgos_privacidad.map((riesgo, i) => (
                        <li key={i} className="text-sm text-red-700">• {riesgo}</li>
                      ))}
                    </ul>
                  </div>
                )}

                {privacidad.derechos_arco && (
                  <div className="bg-green-50 border border-green-200 rounded-lg p-3">
                    <h4 className="text-sm font-medium text-green-800 mb-1">Derechos ARCO:</h4>
                    <p className="text-sm text-green-700">{privacidad.derechos_arco}</p>
                  </div>
                )}
              </div>
            </Section>
          </>
        )}

        {/* Disclaimer */}
        <div className="text-center text-xs text-gray-500 mt-6 p-4 bg-gray-100 rounded-lg">
          <p>Este análisis es informativo y no constituye asesoría legal. Se recomienda consultar con un profesional antes de tomar decisiones.</p>
          <p className="mt-1">Generado por contractcheck360 con tecnología de IA</p>
        </div>

        {/* Action Buttons */}
        <div className="flex flex-wrap justify-center gap-3 mt-6">
          <Link to="/compare">
            <Button style={{ backgroundColor: '#1A4D2E' }}>Comparar Contratos</Button>
          </Link>
          <Link to="/upload">
            <Button variant="outline">Subir Otro Contrato</Button>
          </Link>
          <Link to="/dashboard">
            <Button variant="outline">Dashboard</Button>
          </Link>
        </div>
      </div>
    </div>
  );
};

export default AnalysisResults;
